import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { queryClient } from './api/queryClient';
import { ControlSpecProvider } from './contexts/ControlSpecContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import useAuthUser from './hooks/useAuthUser';
import Sidebar from './components/Sidebar';
import { MaterialityLeaveGuardProvider } from './contexts/MaterialityLeaveGuardContext.tsx';
import NotFoundPage from './components/NotFoundPage';
import AdminRoutes from './admin/routes.tsx';
import SubdomainAuthGuard, { clearAllAuth } from './components/auth/SubdomainAuthGuard.tsx';
import { isAdminSubdomain, redirectToSubdomain, getAppPublicPathUrl } from './utils/tenant';
import { ThemeProvider } from './contexts/ThemeContext.tsx';
import { JobProvider } from './contexts/JobContext';
import { AuditYearProvider } from './contexts/AuditYearContext.tsx';
import { AUTH_PATHS, DEFAULT_PATHS } from './routing/paths.ts';
import { renderMarketingRoutes } from './routing/marketingRoutes.tsx';
import { renderAuthRoutes } from './routing/authRoutes.tsx';
import { renderTenantRoutes } from './routing/tenantRoutes.tsx';
import RiskControlMatrix from './components/RiskControlMatrix';
import RcmImport from './components/RcmImport';
import RcmOrgLibrary from './components/RcmOrgLibrary';
import ProtectedRoute from './components/ProtectedRoute';
import { TooltipProvider } from './admin/components/shared/Tooltip.tsx';
import AITesterPage from './components/ai-tester/AITesterPage';
import { AIEventsProvider } from './contexts/AIEventsContext.tsx';


/** Redirects to app subdomain public path (e.g. /signin, /signup). Use for APP_SUBDOMAIN_PUBLIC_PATHS. */
function RedirectToAppPublic({ path }) {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.location.replace(getAppPublicPathUrl(path));
    }
  }, [path]);
  return null;
}

// Sets or clears the reporting preview flag in localStorage then redirects home.
function ReportingPreviewRoute({ enable = false }) {
  try {
    if (enable) {
      localStorage.setItem('skeptics_reporting_preview', 'true');
    } else {
      localStorage.removeItem('skeptics_reporting_preview');
    }
  } catch { /* storage blocked */ }
  return <Navigate to="/" replace />;
}

function AppContent() {
  const location = useLocation();
  const { clearTenant, setTenant } = useTenant();
  const [user, setUser] = useState(null);
  const hydratedUser = useAuthUser(user);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const isAdminSubdomainView = isAdminSubdomain();

  // Redirect /admin/* on app subdomain to admin.localhost
  useEffect(() => {
    if (!isAdminSubdomainView && location.pathname.startsWith('/admin')) {
      const adminPath = location.pathname.replace(/^\/admin/, '') || DEFAULT_PATHS.ADMIN_LANDING;
      redirectToSubdomain('admin', adminPath);
    }
  }, [isAdminSubdomainView, location.pathname]);

  useEffect(() => {
    const checkAuth = async () => {
      const normalizedPath = location.pathname.replace(/\/$/, '') || '/';
      const isAuthPublicPath =
        normalizedPath === AUTH_PATHS.SIGNIN || normalizedPath === AUTH_PATHS.SIGNUP;

      // On auth public pages, never auto-hydrate from stored credentials.
      // This avoids stale-session dashboard flicker loops before showing signin/signup.
      if (isAuthPublicPath) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Hydrate user from localStorage (set during signin or URL auth consumption) using shared hook.
      if (hydratedUser) {
        setUser(hydratedUser);
      }
      setLoading(false);
    };

    checkAuth();
  }, [setTenant, isAdminSubdomainView, location.pathname, hydratedUser]);

  const handleSignIn = (userData) => {
    setUser(userData);
  };

  const handleSignOut = () => {
    clearAllAuth();
    clearTenant();
    // Always redirect to app signin with full reload - ensures token is cleared and no dashboard flash
    // Use replace to prevent back-button from returning to dashboard
    const signinUrl = getAppPublicPathUrl(`${AUTH_PATHS.SIGNIN}?logout=1`);
    window.location.replace(signinUrl);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Admin subdomain: render admin dashboard (SubdomainAuthGuard redirects to app/signin if no token)
  if (isAdminSubdomainView) {
    return (
      <>
        <Toaster position="top-right" />
        <Routes>
          <Route path="*" element={<AdminRoutes />} />
        </Routes>
      </>
    );
  }

  const normalizedPath = location.pathname.replace(/\/$/, '') || '/';
  const isRiskControlMatrixRoute = normalizedPath === '/risk-control-matrix';
  const rcmMainColumnClasses = isRiskControlMatrixRoute ? 'min-w-0 max-w-full overflow-x-hidden h-screen overflow-y-hidden' : '';

  return (
    <>
      <Toaster position="top-right" />
          <div className="min-h-screen bg-gray-50 flex">
            {user && <Sidebar user={user} onSignOut={handleSignOut} onCollapseChange={setSidebarCollapsed} />}

            <div
              className={`flex-1 transition-all duration-300 ${rcmMainColumnClasses} ${
                user ? (sidebarCollapsed ? 'ml-16' : 'ml-56') : ''
              }`}
            >
              <Routes>
                {renderMarketingRoutes({ user, RedirectToAppPublic })}
                {renderAuthRoutes({ onSignIn: handleSignIn, RedirectToAppPublic })}
                {renderTenantRoutes({ user })}
                <Route path="/ai-tester" element={<AITesterPage />} />
                <Route path="/preview/reporting/enable" element={<ReportingPreviewRoute enable />} />
                <Route path="/preview/reporting/disable" element={<ReportingPreviewRoute />} />
                <Route path="*" element={<NotFoundPage />} />
                 <Route path="/risk-control-matrix" element={
                  <ProtectedRoute user={user}>
                    <RiskControlMatrix />
                  </ProtectedRoute>
                } />
                 <Route path="/risk-control-matrix/import" element={
                  <ProtectedRoute user={user}>
                    <RcmImport />
                  </ProtectedRoute>
                } />
                 <Route path="/risk-control-matrix/org-library" element={
                  <ProtectedRoute user={user}>
                    <RcmOrgLibrary />
                  </ProtectedRoute>
                } />
              </Routes>
            </div>
          </div>
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <ControlSpecProvider>
        <JobProvider>
        <TenantProvider>
          <QueryClientProvider client={queryClient}>
            <AuditYearProvider>
            <TooltipProvider>
              <AIEventsProvider>
              <Router>
                <SubdomainAuthGuard>
                  <MaterialityLeaveGuardProvider>
                    <AppContent />
                  </MaterialityLeaveGuardProvider>
                </SubdomainAuthGuard>
              </Router>
              </AIEventsProvider>
            </TooltipProvider>
            </AuditYearProvider>
          </QueryClientProvider>
        </TenantProvider>
        </JobProvider>
      </ControlSpecProvider>
    </ThemeProvider>
  );
}

export default App;